import {
  computed,
  defineAsyncComponent,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import {
  NavBar as VanNavBar,
  Swipe as VanSwipe,
  SwipeItem as VanSwipeItem,
  NoticeBar as VanNoticeBar,
  Icon as VanIcon,
  Row as VanRow,
  Col as VanCol,
  Popup as VanPopup,
  Button as VanButton,
} from 'vant'
import { useRegionStore } from '@/stores/region-store'
import { useSiteConfigStore } from '@/stores/site-config-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { siteJsonLd } from '@/utils/seo'
import type { Banner, ExternalLink, LiveLotteryData, Notice } from '@/api/types.gen'
import { getApiV1BannersList, getApiV1ExternalLinksList, getApiV1NoticesList } from '@/api/sdk.gen'
import { useWsLiveData, formatNextDrawDate, FakeKeywordsBox } from '@/components/lottery-tabs'
import { BorderHome } from '@/components/border-home'
import { GalleryWaterfall, IconText } from '@/components/gallery-waterfall'
import LiveTopAdBar from '@/components/live-top-ad-bar'
import SharePopup from '@/components/share-popup'
import { ExternalAnchor } from '@/components/external-anchor'
import NAV_SEARCH_ICON from '@/assets/home-nav/search.png'
import NAV_SHARE_ICON from '@/assets/home-nav/share.png'
import NAV_KEFU_ICON from '@/assets/home-nav/kefu.png'

const LiveStreamPlayer = defineAsyncComponent(() => import('@/components/live-stream-player'))

// MARK: 常量

// 首页入口按钮（每行 5 个）
const ENTRIES: Array<{ text: string; img: string; to: string | ((r: string) => string) }> = [
  { text: '开奖现场', img: '/assets/49208/img/kaijiangxianchang.c242fc15.png', to: '/live' },
  { text: '资料大全', img: '/assets/49208/img/ziliaodaquan.d2b418b2.png', to: '/articles' },
  { text: '资讯统计', img: '/assets/49208/img/zixuntongji.bcc27e31.png', to: '/stats' },
  { text: '查询助手', img: '/assets/49208/img/chaxunzhushou.cc2290eb.png', to: '/query-helper' },
  { text: '天线宝宝', img: '/assets/49208/img/txbb.1caa669f.png', to: '/tinky-winky' },
  { text: '幽默猜测', img: '/assets/49208/img/ymjc.14c2e722.png', to: '/humor-guess' },
  {
    text: '六合图库',
    img: '/assets/49208/img/liuhetuku.d32a123c.png',
    to: (r) => `/gallery/search?region=${r}`,
  },
  {
    text: '六合论坛',
    img: '/assets/49208/img/jiaoliudating.508bee0e.png',
    to: (r) => `/forum?region=${r}`,
  },
]

// MARK: 子组件 —— 按 49208 首页 DOM 1:1 复刻

/** 顶部导航栏 */
const HomeHeader = defineComponent({
  setup() {
    const regionStore = useRegionStore()
    const siteConfigStore = useSiteConfigStore()
    const shareRef = ref<ComponentPublicInstance<unknown, { init: () => void }> | null>(null)

    return () => (
      <>
        <VanNavBar fixed clickable={false}>
          {{
            title: () => <div style="font-size:20px">{siteConfigStore.siteName}</div>,
            left: () =>
              siteConfigStore.siteLogo ? (
                <img src={siteConfigStore.siteLogo} style="height:30px" alt="logo" />
              ) : null,
            right: () => (
              <div style="display:flex;height:100%;align-items:self-end;-webkit-tap-highlight-color:transparent">
                <RouterLink
                  to={`/gallery/search?region=${regionStore.region}`}
                  class="btn-reset"
                  style="display:flex;flex-direction:column;align-items:center;cursor:pointer;margin-right:8px"
                >
                  <img src={NAV_SEARCH_ICON} style="width:18px;height:18px" alt="搜索" />
                  <span style="font-size:13px;color:#000">搜索</span>
                </RouterLink>
                <button
                  type="button"
                  class="btn-reset"
                  style="display:flex;flex-direction:column;align-items:center;cursor:pointer;margin-right:8px"
                  onClick={() => shareRef.value?.init()}
                >
                  <img src={NAV_SHARE_ICON} style="width:18px;height:18px" alt="分享" />
                  <span style="font-size:13px;color:#000">分享</span>
                </button>
                <RouterLink
                  to="/contact-customer-service"
                  class="btn-reset"
                  style="display:flex;flex-direction:column;align-items:center;cursor:pointer"
                >
                  <img src={NAV_KEFU_ICON} style="width:18px;height:18px" alt="客服" />
                  <span style="font-size:13px;color:#000">客服</span>
                </RouterLink>
              </div>
            ),
          }}
        </VanNavBar>
        <SharePopup ref={shareRef} />
      </>
    )
  },
})

/** Banner 轮播 */
const HomeBanner = defineComponent({
  props: { banners: { type: Array as () => Banner[], required: true } },
  setup(props) {
    return () => (
      <div class="banner">
        <VanSwipe autoplay={3000} indicatorColor="white">
          {props.banners.map((b) => (
            <VanSwipeItem key={b.id}>
              <div class="item">
                <ExternalAnchor url={b.url ?? ''}>
                  <img src={b.images ?? ''} alt={b.title ?? ''} />
                </ExternalAnchor>
              </div>
            </VanSwipeItem>
          ))}
        </VanSwipe>
      </div>
    )
  },
})

/** 通知栏（跑马灯）—— 点击进入站内信息「消息」中间页 */
const HomeNoticeBar = defineComponent({
  setup() {
    const router = useRouter()
    const noticeText = ref('')

    onMounted(async () => {
      const res = await getApiV1NoticesList({ query: { page: 1, size: 999, status: 1 } })
      const list: Notice[] = res.data?.data?.list ?? []
      if (list.length > 0) {
        noticeText.value = list.map((n) => n.notice_content ?? '').join('     ·     ')
      }
    })

    return () => (
      <VanNoticeBar
        text={noticeText.value}
        background="#fff"
        color="#333"
        class="cursor-pointer"
        onClick={() => router.push('/sitemsg')}
      >
        {{
          'left-icon': () => <VanIcon name="volume" color="#ef5151" size="14" />,
        }}
      </VanNoticeBar>
    )
  },
})

/** 彩票区块（49208 皮肤）—— DOM/CSS 全部对齐 49208，业务逻辑（WS 实时数据 + 直播播放器）复用本项目 */
const HomeBlockLottery = defineComponent({
  setup() {
    const regionStore = useRegionStore()
    const hkData = ref<LiveLotteryData | null>(null)
    const aocaiData = ref<LiveLotteryData | null>(null)
    const hkDrawingMode = ref(false)
    const aocaiDrawingMode = ref(false)

    const { connectBoth, reconnect, hkWsActive, aocaiWsActive } = useWsLiveData(
      hkData,
      aocaiData,
      hkDrawingMode,
      aocaiDrawingMode,
    )

    onMounted(() => connectBoth())

    // 手动展开/收起直播（undefined = 由 drawingMode 自动决定）
    const manualShow = ref<boolean | undefined>(undefined)

    const currentData = computed(() =>
      regionStore.lotteryType === 'hk' ? hkData.value : aocaiData.value,
    )
    const drawingMode = computed(() =>
      regionStore.lotteryType === 'hk' ? hkDrawingMode.value : aocaiDrawingMode.value,
    )
    const liveOn = computed(() => manualShow.value ?? drawingMode.value)
    const wsActive = computed(() =>
      regionStore.lotteryType === 'hk' ? hkWsActive.value : aocaiWsActive.value,
    )

    // 仅在开奖直播期间（drawingMode 为 true）WS 持续断开才显示重连按钮：
    //   - 非开奖期间断线属于正常现象，不需要提示用户
    //   - 延迟 8 秒 = 3s 自动重连间隔 + ~5s 连接超时，确保至少一次重连已尝试后再显示
    const wsDisconnected = ref(false)
    let wsDisconnectTimer: ReturnType<typeof setTimeout> | undefined
    watch([wsActive, drawingMode], ([active, drawing]) => {
      clearTimeout(wsDisconnectTimer)
      if (active || !drawing) {
        // WS 已连上，或不在开奖直播期间 → 隐藏按钮
        wsDisconnected.value = false
      } else {
        // 开奖期间 + WS 断开 → 等待一次重连周期后再提示
        wsDisconnectTimer = setTimeout(() => {
          wsDisconnected.value = true
        }, 8000)
      }
    })
    onUnmounted(() => clearTimeout(wsDisconnectTimer))

    const platforms: Array<{ type: 'hk' | 'macau'; name: string }> = [
      { type: 'macau', name: '澳彩' },
      { type: 'hk', name: '港彩' },
    ]

    function platformTime(type: 'hk' | 'macau') {
      const d = type === 'hk' ? hkData.value : aocaiData.value
      if (!d?.next_draw_date) return '--开奖'
      return `${formatNextDrawDate(d.next_draw_date)}开奖`
    }

    return () => (
      <div class="block-lottery">
        {/* 港澳彩切换 */}
        <ul class="platform-list">
          {platforms.map((p) => (
            <li key={p.type}>
              <button
                type="button"
                class={['item-body btn-block', { act: p.type === regionStore.lotteryType }]}
                onClick={() => regionStore.setLotteryType(p.type)}
              >
                <span class="title block">
                  <b>{p.name}</b>
                </span>
                <span class="time">
                  <span>{platformTime(p.type)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>

        {/* 视频播放区 */}
        <div class="v-player">
          <VanRow class="home-shad" style="margin-bottom:0;padding-bottom:8px;border-radius:0">
            <VanCol span={24} style="position:relative">
              <VanRow>
                <VanCol span={24} class={liveOn.value ? '' : 'openLives'}>
                  <div class="video" style="height:240px;background:#000;position:relative">
                    {drawingMode.value && <LiveTopAdBar />}
                    {liveOn.value ? (
                      <LiveStreamPlayer lotteryType={regionStore.lotteryType} />
                    ) : (
                      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;text-align:center;background:rgba(0,0,0,0.55);color:#fff;z-index:1;padding:12px;flex-direction:column">
                        <div style="line-height:1.6">
                          <div
                            class="message-content"
                            style="font-size:14px;line-height:1.5;max-width:420px;color:red"
                          >
                            {regionStore.lotteryType === 'macau' ? '澳门六合彩' : '香港六合彩'}
                            攪珠直播時間為每晚 <b>21:30-21:45</b>（北京時間），敬請查看
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </VanCol>
              </VanRow>
            </VanCol>
          </VanRow>
          <div class="v-open-hide">
            <button type="button" class="btn-reset" onClick={() => (manualShow.value = !liveOn.value)}>
              <span class={{ open: liveOn.value }}>
                <label>{liveOn.value ? '收起直播' : '展开直播'}</label>
              </span>
            </button>
          </div>
        </div>

        {/* 开奖号码栏 / 开奖期间 WS 持续断开超过 8 秒后显示重连按钮 */}
        {wsDisconnected.value ? (
          <div class="flex flex-col items-center justify-center py-6 gap-2">
            <span class="text-sm text-gray-400">数据连接已断开</span>
            <VanButton
              type="primary"
              size="small"
              onClick={() => reconnect(regionStore.lotteryType)}
            >
              重新连接
            </VanButton>
          </div>
        ) : (
          <BorderHome data={currentData.value} lotteryType={regionStore.lotteryType} />
        )}
      </div>
    )
  },
})

const FitText = defineComponent({
  props: { text: { type: String, required: true } },
  setup(props) {
    const elRef = ref<HTMLParagraphElement>()
    const extra = ref<{ fontSize?: string; textOverflow?: string } | undefined>()
    let alive = true
    onUnmounted(() => {
      alive = false
    })

    async function fit() {
      extra.value = undefined
      await nextTick()
      if (!alive) return
      const el = elRef.value
      if (!el) return
      // 只处理 6-7 字：缩小字号完整展示，其余保持 CSS 默认（≤5字不变，≥8字省略号截断）
      const len = props.text.length
      if (len < 6 || len > 7) return
      if (el.scrollWidth <= el.clientWidth) return
      const natural = Number.parseFloat(getComputedStyle(el).fontSize)
      const fitted = Math.max(natural * (el.clientWidth / el.scrollWidth), 8)
      extra.value = { fontSize: `${fitted.toFixed(1)}px`, textOverflow: 'clip' }
    }

    onMounted(fit)
    watch(() => props.text, fit)

    return () => (
      <p ref={elRef} style={extra.value}>
        {props.text}
      </p>
    )
  },
})

/** 百度关键词推荐站点栏（对应 49208 .website-body）— 接 /api/v1/external-links/list */
const HomeWebsiteBody = defineComponent({
  setup() {
    const regionStore = useRegionStore()
    const links = ref<ExternalLink[]>([])

    async function fetchLinks() {
      const lotteryType = regionStore.region === 'hk' ? '港彩' : '澳彩'
      try {
        const res = await getApiV1ExternalLinksList({
          query: { page: 1, size: 100, status: 1, lotteryType },
        })
        links.value = res.data?.data?.list ?? []
      } catch {
        links.value = []
      }
    }

    onMounted(fetchLinks)
    watch(() => regionStore.region, fetchLinks)

    return () => (
      <div class="website-body clearfix">
        {links.value.map((item) => (
          <ExternalAnchor key={item.id} class="website-item" url={item.link_url ?? ''}>
            <div>
              <FitText text={item.link_title ?? ''} />
            </div>
          </ExternalAnchor>
        ))}
      </div>
    )
  },
})

/** 入口按钮栏（10 个，每行 5 个） */
const HomeCategoryTr = defineComponent({
  setup() {
    const router = useRouter()
    const regionStore = useRegionStore()
    return () => (
      <div class="category-tr">
        <VanRow class="category-icon">
          {ENTRIES.map((e) => (
            <VanCol
              key={e.text}
              span={5}
              onClick={() => {
                const path = typeof e.to === 'function' ? e.to(regionStore.region) : e.to
                router.push(path)
              }}
            >
              <IconText text={e.text}>
                <div class="icon-bg">
                  <img src={e.img} alt={e.text} />
                </div>
              </IconText>
            </VanCol>
          ))}
        </VanRow>
      </div>
    )
  },
})

// MARK: 欢迎弹窗（首次访问，type=3）—— 对应 49208 的 .tan-pop

const WELCOME_SHOWN_KEY = 'nb_welcome_shown'

const HomeWelcomeModal = defineComponent({
  setup() {
    const show = ref(false)
    const banners = ref<Banner[]>([])

    onMounted(async () => {
      if (localStorage.getItem(WELCOME_SHOWN_KEY) === '1') return
      const res = await getApiV1BannersList({
        query: { page: 1, size: 999, status: 1, type: 3 },
      })
      const list = res.data?.data?.list ?? []
      if (list.length === 0) return
      banners.value = list
      show.value = true
    })

    function bindClose() {
      show.value = false
    }
    function bindSee() {
      show.value = false
      localStorage.setItem(WELCOME_SHOWN_KEY, '1')
    }

    return () => (
      <VanPopup
        class="tan-pop"
        show={show.value}
        round
        closeOnClickOverlay={false}
        position="center"
        style="width:80%"
        onUpdate:show={(v: boolean) => (show.value = v)}
      >
        <div class="t-myheader">重要通知</div>
        <VanSwipe autoplay={3000} indicatorColor="#07C160" autoHeight>
          {banners.value.map((b) => (
            <VanSwipeItem key={b.id} class="tan-swipe">
              {b.images && (
                <div class="box-img">
                  <ExternalAnchor url={b.url ?? ''} onClick={() => b.url && (show.value = false)}>
                    <img src={b.images} alt="" />
                  </ExternalAnchor>
                </div>
              )}
            </VanSwipeItem>
          ))}
        </VanSwipe>
        <div class="t-footer">
          <span>
            <button type="button" class="btn-block h-full" onClick={bindClose}>
              {' '}
              关闭
            </button>
          </span>
          <span class="bindSee">
            <button type="button" class="btn-block h-full" onClick={bindSee}>
              {' '}
              不再提醒
            </button>
          </span>
        </div>
      </VanPopup>
    )
  },
})

// MARK: 首页（默认导出）—— 按 49208 根结构

const Home = defineComponent({
  name: 'HomePage',
  setup() {
    const banners = ref<Banner[]>([])

    // SEO：首页配站点级文案 + WebSite/Organization 结构化数据（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `${brand.value} - 澳门六合彩开奖结果_香港六合彩开奖直播_六合图库资料大全`,
      description: () =>
        `${brand.value}提供澳门六合彩、香港六合彩实时开奖结果与开奖直播，含历史开奖记录、走势图、六合图库、资料大全与论坛交流，数据快速准确。`,
      keywords: () =>
        `澳门六合彩,香港六合彩,六合彩开奖结果,开奖直播,六合图库,走势图,资料大全,六合论坛,${brand.value}`,
      jsonLd: () => siteJsonLd(brand.value),
    })

    async function fetchHomeBanners() {
      const res = await getApiV1BannersList({
        query: { page: 1, size: 999, status: 1, type: 1 },
      })
      banners.value = res.data?.data?.list ?? []
    }

    onMounted(fetchHomeBanners)

    return () => (
      <div class="home">
        <HomeHeader />
        <HomeBanner banners={banners.value} />
        <HomeNoticeBar />
        <HomeBlockLottery />
        <HomeWebsiteBody />
        <FakeKeywordsBox type={1} />
        <div class="border-line" />
        <HomeCategoryTr />
        <GalleryWaterfall storeKey="home" stickyOffsetTop={46} />
        <HomeWelcomeModal />
      </div>
    )
  },
})

export default Home
